<template>
  <v-row>
    <v-col cols="12" :md="contractHasTract ? 4 : (hasTicketBooks ? 4 : 6)" :xl="contractHasTract ? 6 : (hasTicketBooks ? 4 : 6)">
      <DetailCard :title="$t('properties')">
        <template #title.append>
          <v-row no-gutters>
            <v-col cols="auto" v-for="attribute in attributes" :key="attribute.text">
              <Icon
                :key="attribute.text"
                :icon="attribute.icon"
                :tooltipText="attribute.text"
                :iconColor="attribute.color"
                :tooltipColor="attribute.color"
              />
            </v-col>
          </v-row>
        </template>

        <v-row>
          <v-col cols="12" xl="auto">
            <v-card color="grey lighten-4" flat class="fill-height">
              <v-card-text>
                <v-row align="center" justify="space-between" no-gutters v-for="(contractKey, i) in contractKeys" :key="i" class="flex-nowrap">
                  <v-col cols="auto">
                    <Icon margin="mr-2" :icon="IconHelpers.getIconForObjectType(contractKey)" iconColor="grey darken-1" :small="false" :tooltipText="$t(contractKey)"></Icon>
                  </v-col>

                  <v-col style="text-wrap: nowrap; overflow: hidden;" class="text-right">
                    <Icon v-if="keyIsCertified(contractKey)" icon="mdi-certificate-outline" iconColor="info" :small="false" :tooltipText="$t('certified')"></Icon>

                    <v-tooltip bottom color="grey darken-1">
                      <template #activator="{on}">
                        <span v-on="on" class="subtitle-1">{{contract[contractKey]}}</span>
                      </template>
                      <span class="subtitle-1 white--text">{{codeByContractKey(`${contractKey}Code`)}}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" xl="" :class="$vuetify.breakpoint.xl ? 'px-6' : ''">
            <v-row dense data-testid="contract-details-prop-table">
              <v-col cols="12">
                <v-row no-gutters>
                  <v-col> {{$t('type')}}: </v-col>
                  <v-col> {{contractTypeFromInt(contract.type)}} </v-col>
                </v-row>
              </v-col>

              <v-col v-if="contractHasTract" cols="12">
                <v-row no-gutters>
                  <v-col> {{$t('tractType')}}: </v-col>
                  <v-col> {{contract.tractType}} </v-col>
                </v-row>
              </v-col>

              <v-col v-if="contractHasTract" cols="12">
                <v-row no-gutters>
                  <v-col> {{$t('logger')}}: </v-col>
                  <v-col> {{contract.tractDefaultLogger ?? $t('notAvailable')}} </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" v-if="contract.distance > 0">
                <v-row no-gutters>
                  <v-col> {{$t('distance')}}: </v-col>
                  <v-col> {{ contract.distance }} </v-col>
                </v-row>
              </v-col>

              <v-col cols="12" v-if="contract.tareWeight">
                <v-row no-gutters>
                  <v-col> {{$t('tareWeight')}}: </v-col>
                  <v-col> {{ numberWithCommas(contract.tareWeight) }} {{ $t('lbsUpper') }}</v-col>
                </v-row>
              </v-col>

              <v-col cols="12" v-if="showAllowPickupLoads">
                <v-row no-gutters>
                  <v-col> {{ $t('allowsLoadPickup')}}: </v-col>
                  <v-col>
                    <Icon
                      :tooltipText="contract.allowPickupLoads ? $t('allowsLoadPickup') : $t('notAllowsLoadPickup')"
                      :icon="contract.allowPickupLoads ? 'mdi-checkbox-marked-circle' : 'mdi-close'"
                      :iconColor="contract.allowPickupLoads ? 'success' : 'error'"
                    />
                  </v-col>
                </v-row>
              </v-col>

              <v-col cols="12">
                <v-row no-gutters>
                  <v-col> {{$t('active')}}: </v-col>
                  <v-col>
                    <span>{{utcToLocalDate(contract.effectiveDate)}} - </span>

                    <span>{{utcToLocalDate(contract.expirationDate)}}</span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </DetailCard>
    </v-col>

    <v-col cols="12" sm="6" md="4" xl="3" v-if="contractHasTract">
      <DetailCard :title="$t('tractLandowners')">
        <v-data-table
          :mobile-breakpoint="0"
          fixed-header
          :height="$vuetify.breakpoint.smAndDown ? 'auto' : `${ $vuetify.breakpoint.xl ? 150 : 310 }px`"
          dense
          @click:row="openLandownerDialog"
          :items-per-page="-1"
          hide-default-footer
          :items="tractLandowners"
          :headers="[{text: $t('name'), value: 'accountName'},{text: $t('ownership'), value: 'ownership', align: 'right'}]"
          class="small-headers"
        >
          <template #item.ownership="{item}">
            {{ formatPercentage(item.ownership) }}
          </template>
        </v-data-table>
      </DetailCard>
    </v-col>

    <v-col cols="12" sm="6" :md="contractHasTract ? 4 : (hasTicketBooks ? 4 : 6)" :xl="contractHasTract ? 3 : (hasTicketBooks ? 4 : 6)">
      <DetailCard :title="$t('products')">
        <template #title.append>
          <Icon
          icon="mdi-pencil"
          :tooltipText="$t('editProducts')"
          @icon-clicked="editProducts"
          :disabled="readonly"/>
        </template>

        <v-data-table
          :mobile-breakpoint="0"
          v-if="products.length > 0"
          fixed-header
          :height="$vuetify.breakpoint.smAndDown ? 'auto' : `${ $vuetify.breakpoint.xl ? 150 : 310 }px`"
          dense
          @click:row="openProductDialog"
          :items-per-page="-1"
          hide-default-footer
          :items="products"
          :headers="productTableHeaders"
          class="small-headers"
        >
          <template #item.scaleFrequency="{ item }">
            <Icon
              v-if="getDisplayFrequencyForTooltip(item) !== null"
              icon="mdi-crosshairs-gps"
              :small="false"
              iconColor="black"
              :tooltipText="$t('destinationScaleFrequency', { frequency: getDisplayFrequencyForTooltip(item) })"
            />
            <span v-if="item.scaleFrequency !== null && item.scaleFrequency > 0" class="body-1">
              {{ convertPercentToRatio(item.scaleFrequency) }}
            </span>
          </template>

          <template #header.grossProfit="{header}">
            <v-row align="center" no-gutters justify="end">
              <v-col cols="auto" v-if="showPayByLoadWarning">
                <Icon
                  iconColor="info"
                  icon="mdi-information-outline"
                  :tooltipText="$t('profitPerTonCannotBeCalculatedForPayByLoadEditing')"
                />
              </v-col>

              <v-col cols="auto" v-if="showNonInventoryWarning">
                <Icon
                  iconColor="info"
                  icon="mdi-information-outline"
                  :tooltipText="$t('nonInventoryCostsExcluded')"
                />
              </v-col>

              <v-col cols="auto">
                <span>{{ header.text }}</span>
              </v-col>
            </v-row>
          </template>

          <template #item.grossProfit="{item}">
            {{ getGrossProfitColumn(item) }}
          </template>

          <template #item.name="{item}">
            <div :style="productNameCellStyle">
              <span >{{item.name}}</span>
            </div>
          </template>
        </v-data-table>

        <span v-else>{{$t('noProductsSpecified')}}</span>
      </DetailCard>
    </v-col>

    <v-col cols="12" sm="6" md="4" xl="4" v-if="hasTicketBooks">
      <DetailCard :title="$t('ticketBooks')">
        <v-data-table
          :mobile-breakpoint="0"
          fixed-header
          dense
          :items-per-page="-1"
          hide-default-footer
          :items="mappedTicketBooks"
          :headers="ticketBookHeaders"
          class="small-headers"
        >
          <template #item.used="{ item }">
            {{ ticketBookUsage(item) }}
          </template>
        </v-data-table>
      </DetailCard>
    </v-col>

    <Dialog :stateId="dialogId" @dialog-closing="closeDialog">
      <ProductDialog
      v-if="productDialog"
      :products="sortedProducts"
      :profitByProduct="profitByProduct"
      :contract="contract"
      @close="closeDialog"/>
      <v-card v-if="landownerDialog">
        <v-card-title class="primary">
          <v-row>
            <v-col>
              <span class="headline white--text">
                {{ $t('landowners') }}
              </span>
            </v-col>

            <v-col cols="auto">
              <BaseDialogActions hideRefresh hideFullscreen @close="closeDialog"/>
            </v-col>
          </v-row>
        </v-card-title>

        <v-card-text>
          <v-container fluid style="max-height: 60vh; overflow-y: auto;">
            <v-data-table
              :mobile-breakpoint="0"
              fixed-header
              dense
              :items-per-page="-1"
              hide-default-footer
              :items="tractLandowners"
              :headers="[{text: $t('name'), value: 'accountName'},{text: $t('ownership'), value: 'ownership', align: 'right'}]"
              class="small-headers"
            >
              <template #item.ownership="{item}">
                {{ formatPercentage(item.ownership) }}
              </template>
            </v-data-table>
          </v-container>
        </v-card-text>
      </v-card>
      <ContractProductForm
      @close="closeContractProductForm"
      :contract="contract"
      :products="sortedProducts"
      :activities="allActivities"
      v-if="contractProductForm"/>
    </Dialog>
  </v-row>
</template>

<script>
import ProductHeaders from '@/headers/Product'
import { numberWithCommas, formatMoney, formatPercentage, pageRangeString } from '@/utils/NumericMutations'
import { ContractType, PayBy, TemplateCostType, CertificationStatus } from '@/utils/Enumerations.js'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { profitByProductMap, hasPayByVolumeInventoryActivity } from '@/utils/grossProfit.js'
import { mapActions, mapGetters } from 'vuex'
import IconHelpers from '@/utils/IconHelpers'

export default {
  name: 'ContractInformation',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    ProductDialog: () => import('@/components/contract/contract-detail/ProductDialog.vue'),
    ContractProductForm: () => import('@/components/contract/contract-detail/ContractProductForm.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue')
  },

  props: {
    contract: {
      required: true,
      type: Object
    },
    allActivities: {
      type: Array,
      default: undefined,
      required: true
    },
    tractLandowners: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    IconHelpers,
    productDialog: false,
    landownerDialog: false,
    profitByProduct: undefined,
    dialogId: uniqueDialogId('product-dialog'),
    contractProductForm: false,
    products: [],
    tractDestinationProducts: []
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),
    contractTypeIsWoodsSale () {
      return this.contract.type === ContractType.WoodsSale.value
    },

    contractTypeIsByproduct () {
      return this.contract.type === ContractType.ByproductSale.value
    },

    showAllowPickupLoads () {
      if (this.contractTypeIsByproduct) return true
      return this.contract.type === ContractType.LogYardSale.value &&
        (this.contract.allowPickupLoads === true || this.allActivities.some(a => a.baseCost?.payBy === PayBy.Volume.value))
    },

    productHeaders () { return ProductHeaders.productHeaders(this.$i18n.locale) },

    attributes () {
      const attributes = []

      if (!this.contract.isExternal) {
        attributes.push({
          text: this.$t('internal'),
          icon: 'mdi-home',
          color: 'black'
        })
      }

      if (this.contract.isDraft) {
        attributes.push({
          text: this.contract.hasPendingTickets ? this.$t('hasPendingTickets') : this.$t('draftContract'),
          icon: 'mdi-file',
          color: this.contract.hasPendingTickets ? 'error' : 'black'
        })
      }

      if (this.contract.requiresLogger) {
        attributes.push({
          text: this.$t('requiresLogger'),
          icon: 'mdi-axe',
          color: 'brown'
        })
      }

      if (this.contract.requiresExt1) {
        attributes.push({
          text: this.$t('requiresExt1'),
          icon: 'mdi-pound',
          color: 'info'
        })
      }

      if (this.contract.requiresExt2) {
        attributes.push({
          text: this.$t('requiresExt2'),
          icon: 'mdi-pound',
          color: 'info'
        })
      }

      return attributes
    },

    isLogsContract () {
      return this.contract?.type === ContractType.Production.value || this.contract?.type === ContractType.WoodsSale.value
    },

    contractHasTract () {
      return this.isLogsContract ||
        (this.contract?.type === ContractType.ByproductPurchase.value && this.contract.tract)
    },

    contractHasFromAccount () {
      return !this.isLogsContract && this.contract.fromAccountId
    },

    showGrossProfit () {
      return this.contractTypeIsWoodsSale && !hasPayByVolumeInventoryActivity(this.allActivities)
    },

    showPayByLoadWarning () {
      return this.allActivities?.flatMap(a => a.activityDetails).some(ad => ad.cost.payBy === PayBy.Load.value)
    },

    showNonInventoryWarning () {
      return this.allActivities?.some(a => a.activityTemplate.costType === TemplateCostType.nonInventoryCost.value)
    },

    isCertified () {
      return CertificationStatus.isCertified(this.contract.accountCertificationStatus)
    },

    contractKeys () {
      var result = ['destination']
      if (this.contractHasFromAccount) {
        result.unshift('account')
        result.unshift('fromAccount')
      }
      if (this.contractHasTract) result.unshift('tract')
      if (this.isLogsContract) result.push('setting')
      if (this.contractHasTract) result.push('account')
      return result
    },

    productTableHeaders () {
      var result = [{ text: this.$t('name'), value: 'name' }]
      if (this.showGrossProfit) result.push({ text: this.$t('grossProfit'), value: 'grossProfit', align: 'right', sortable: false })
      if (this.products.some(p => p.scaleFrequency !== null && p.scaleFrequency > 0)) result.push({ text: this.$t('scaleFrequency'), value: 'scaleFrequency', align: 'right' })
      return result
    },

    productNameCellStyle () {
      return this.productTableHeaders.length > 1 ? { maxWidth: '100px' } : undefined
    },

    sortedProducts () {
      return this.products.toSorted((a, b) => a.sortOrder - b.sortOrder)
    },

    ticketBookHeaders () {
      return [
        {
          text: this.$t('name'),
          value: 'name'
        },
        {
          text: this.$t('pages'),
          value: 'pages'
        },
        {
          text: this.$t('used'),
          value: 'used'
        }
      ]
    },

    mappedTicketBooks () {
      return (this.contract.ticketBooks ?? []).map(tb => ({
        ...tb,
        pages: pageRangeString(tb.firstPage, tb.lastPage)
      }))
    },

    hasTicketBooks () {
      return this.mappedTicketBooks.length > 0
    },

    contractRequiresEditApproval () {
      return this.companyInfo.requireApprovalForContractModifications && !this.contract.isDraft
    }
  },

  async created () {
    if (this.contractHasTract) {
      const tractDestinations = await this.fetchTractDestinations(this.contract.tractId)
      const contractDestination = tractDestinations.find(td => td.destinationAccountId === this.contract.destinationAccountId)
      this.tractDestinationProducts = contractDestination?.products
    }

    this.products = this.contract.products
    if (this.showGrossProfit) this.initializeProfitByProductsMap()
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract-destination', ['fetchTractDestinations']),
    utcToLocalDate,
    numberWithCommas,
    formatMoney,
    formatPercentage,
    contractTypeFromInt: (type) => ContractType.fromInt(type),

    getProductProfitRate (product) {
      return this.profitByProduct.get(product.productId)
    },

    initializeProfitByProductsMap () {
      if (this.showPayByLoadWarning) return undefined
      this.profitByProduct = profitByProductMap(this.allActivities, this.products, this.contract.distance)
    },

    openProductDialog () {
      this.closeDialog()
      this.productDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '600' })
    },

    closeDialog () {
      this.productDialog = false
      this.landownerDialog = false
      this.contractProductForm = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openLandownerDialog () {
      this.closeDialog()
      this.landownerDialog = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    keyIsCertified (contractKey) {
      switch (contractKey) {
        case 'account': return this.contract.accountCertificationStatus === 1
        case 'tract': return this.contract.tractIsCertified
        default: return false
      }
    },

    getGrossProfitColumn (item) {
      return !this.showPayByLoadWarning ? `${formatMoney(this.getProductProfitRate(item))}/t` : ''
    },

    editProducts () {
      this.closeDialog()
      if (this.contractRequiresEditApproval) { // Re-enter through ContractEditor to confirm approval creation
        this.$emit('edit-products')
        return
      }
      this.contractProductForm = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '400' })
    },

    closeContractProductForm (products) {
      if (products) this.products = products
      this.closeDialog()
    },

    codeByContractKey (contractKey) {
      if (this.contract[contractKey] !== null) {
        return `${this.$t('code')}: ${this.contract[contractKey]}`
      } else {
        return `${this.$t('code')}: ${this.$t('notSpecified')}`
      }
    },

    ticketBookUsage (book) {
      const usagePercentage = book.totalPageCount > 0 ? (book.totalPageCount - book.availablePageCount) / book.totalPageCount * 100 : 0
      return `${usagePercentage.toFixed(2)}%`
    },

    convertPercentToRatio (percent) {
      const p = parseFloat(percent)
      if (!isFinite(p) || p <= 0) return ''
      const denom = Math.round(100 / p)
      return `1:${denom}`
    },

    getDisplayFrequencyForTooltip ({ productId, scaleFrequency }) {
      const product = this.tractDestinationProducts?.find(p => p.productId === productId)

      if (product == null || product.scaleFrequency <= 0 || scaleFrequency > 0) return null

      return this.convertPercentToRatio(product.scaleFrequency)
    }
  }
}
</script>
